import type { PeriodUnit } from '@apis/summary';
import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

/**
 * Convert a timestamp to a formatted date string.
 *
 * Notes:
 * - Prefer using `formatApiDateToKst` / the KST range helpers below for dashboard-related
 *   date display and API ranges.
 * - This function is kept for backward compatibility with existing usage.
 */
export const timestampToDate = (
  timestamp: string | number,
  fmt?:
    | 'YYYY-MM-DD'
    | 'YYYY/MM/DD'
    | 'DD-MM-YYYY'
    | 'DD/MM/YYYY'
    | 'YYYYMMDD'
): string => {
  if (!timestamp) return '';

  // `Date` will interpret a number as epoch milliseconds (UTC-based).
  // The legacy behavior adds +9h when the input is a number.
  let date = new Date(timestamp);
  if (typeof timestamp !== 'string') {
    date = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  }

  const iso = date.toISOString();

  switch (fmt) {
    case 'YYYY-MM-DD':
      return iso.slice(0, 10);
    case 'YYYY/MM/DD':
      return iso.slice(0, 10).replace(/-/g, '/');
    case 'DD-MM-YYYY':
      return `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}`;
    case 'DD/MM/YYYY':
      return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
    case 'YYYYMMDD':
      return `${iso.slice(0, 4)}${iso.slice(5, 7)}${iso.slice(8, 10)}`;
    default:
      return iso.slice(0, 19).replace('T', ' ');
  }
};

/**
 * Convert a timestamp to a formatted date-time string.
 *
 * Kept for backward compatibility with existing usage.
 */
export const timestampToDateTime = (
  timestamp: string | number,
  fmt = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!timestamp) return '';

  let date = new Date(timestamp);
  if (typeof timestamp !== 'string') {
    date = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  }

  const iso = date.toISOString();
  if (fmt === 'YYYY-MM-DD HH:mm:ss') return iso.slice(0, 19).replace('T', ' ');
  return iso.slice(0, 19).replace('T', ' ');
};

/**
 * Convert a date string (yyyy-MM-dd) to an epoch timestamp.
 *
 * Params:
 * - endOfDay: if true, uses 23:59:59 for the time portion
 * - startOfDay: if true, uses 00:00:00 for the time portion
 * - truncate: when true, returns UTC epoch milliseconds for the constructed KST time
 *
 * This helper is used by dashboard range builders to generate UTC timestamps
 * representing KST boundaries.
 */
export const dateToTimestamp = (
  date: string,
  endOfDay: boolean = false,
  truncate: boolean = false,
  startOfDay: boolean = false
): number => {
  let timePortion = endOfDay ? 'T23:59:59' : '';
  if (startOfDay) timePortion = 'T00:00:00';

  const dateWithTime = `${date}${timePortion}`;

  // Interpret the input as a KST-local date/time.
  let zonedDate = utcToZonedTime(dateWithTime, KOREAN_TIME_ZONE);

  // Convert the KST-local moment back to a UTC timestamp when needed.
  if (truncate) {
    zonedDate = new Date(zonedDate.getTime() - 9 * 60 * 60 * 1000);
  }

  const formattedDate = format(
    zonedDate,
    `yyyy-MM-dd${endOfDay || startOfDay ? ' HH:mm:ss' : ''}`
  );

  const finalDate = new Date(
    `${formattedDate}${endOfDay || startOfDay ? 'Z' : ''}`
  );

  return finalDate.getTime();
};

/**
 * Get a [from, to] timestamp range for a period unit based on the local runtime date.
 *
 * Kept for backward compatibility with existing usage.
 * For dashboard KPI ranges (KST-aware), prefer the KST helpers below.
 */
export const getTimestampRangeByPeriod = (
  period: PeriodUnit
): { from: number; to: number } => {
  let from = new Date();
  let to = new Date();

  if (period === 'DAYS') {
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
  }

  if (period === 'WEEKS') {
    from = new Date(from.setDate(from.getDate() - from.getDay()));
    from.setHours(0, 0, 0, 0);

    to = new Date(to.setDate(to.getDate() + (6 - to.getDay())));
    to.setHours(23, 59, 59, 999);
  }

  if (period === 'MONTHS') {
    from.setDate(1);
    from.setHours(0, 0, 0, 0);

    to.setMonth(to.getMonth() + 1);
    to.setDate(0);
    to.setHours(23, 59, 59, 999);
  }

  if (period === 'YEARS') {
    from.setMonth(0);
    from.setDate(1);
    from.setHours(0, 0, 0, 0);

    to.setMonth(11);
    to.setDate(31);
    to.setHours(23, 59, 59, 999);
  }

  return { from: from.getTime(), to: to.getTime() };
};

/* ============================================================================
 * Dashboard helpers (KST-based)
 * ========================================================================== */

/**
 * Returns today's date string in KST (yyyy-MM-dd).
 */
export const getKstTodayString = (): string => {
  const nowUtc = new Date();
  const nowKst = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);
  return format(nowKst, 'yyyy-MM-dd');
};

/**
 * Shift a KST date string (yyyy-MM-dd) by diffDays and return a new yyyy-MM-dd.
 *
 * The calculation is performed relative to KST midnight to avoid runtime locale
 * timezone side effects.
 */
export const shiftKstDateString = (baseYmd: string, diffDays: number): string => {
  const baseKst = utcToZonedTime(`${baseYmd}T00:00:00`, KOREAN_TIME_ZONE);
  baseKst.setDate(baseKst.getDate() + diffDays);
  return format(baseKst, 'yyyy-MM-dd');
};

/**
 * Rolling N-day range (KST), with an option to exclude today.
 *
 * Returns:
 * - from: KST start-of-day converted to a UTC epoch timestamp
 * - to  : KST end-of-day converted to a UTC epoch timestamp
 */
export const getTimestampRangeRollingDaysKst = (
  days: number,
  excludeToday: boolean = false
): { from: number; to: number } => {
  const today = getKstTodayString();
  const toYmd = excludeToday ? shiftKstDateString(today, -1) : today;
  const fromYmd = shiftKstDateString(toYmd, -(days - 1));

  return {
    from: dateToTimestamp(fromYmd, false, true, true),
    to: dateToTimestamp(toYmd, true, true, false),
  };
};

/**
 * Dashboard KPI presets (KST):
 * - TODAY: today's full day window (00:00:00 ~ 23:59:59)
 * - R7   : last 7 days excluding today
 * - R30  : last 30 days excluding today
 */
export const getDashboardKpiRangeKst = (
  preset: 'TODAY' | 'R7' | 'R30'
): { from: number; to: number } => {
  if (preset === 'TODAY') {
    const today = getKstTodayString();
    return {
      from: dateToTimestamp(today, false, true, true),
      to: dateToTimestamp(today, true, true, false),
    };
  }

  if (preset === 'R7') return getTimestampRangeRollingDaysKst(7, true);
  return getTimestampRangeRollingDaysKst(30, true);
};

/**
 * "To date" ranges (KST):
 * - WTD: week start (Sun) 00:00:00 ~ today 23:59:59
 * - MTD: month start 00:00:00 ~ today 23:59:59
 * - YTD: year start 00:00:00 ~ today 23:59:59
 */
export const getTimestampRangeToDateKst = (
  period: 'WEEKS' | 'MONTHS' | 'YEARS'
): { from: number; to: number } => {
  const today = getKstTodayString();
  const nowUtc = new Date();
  const nowKst = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);

  let fromYmd = today;

  if (period === 'WEEKS') {
    const start = new Date(nowKst.getFullYear(), nowKst.getMonth(), nowKst.getDate());
    start.setDate(start.getDate() - start.getDay());
    fromYmd = format(start, 'yyyy-MM-dd');
  }

  if (period === 'MONTHS') {
    const start = new Date(nowKst.getFullYear(), nowKst.getMonth(), 1);
    fromYmd = format(start, 'yyyy-MM-dd');
  }

  if (period === 'YEARS') {
    const start = new Date(nowKst.getFullYear(), 0, 1);
    fromYmd = format(start, 'yyyy-MM-dd');
  }

  return {
    from: dateToTimestamp(fromYmd, false, true, true),
    to: dateToTimestamp(today, true, true, false),
  };
};

/**
 * Format an API-provided ISO-like timestamp as KST.
 * Used by dashboard chart labels and tooltips to ensure a consistent timezone.
 */
export const formatApiDateToKst = (
  isoLike: string,
  fmt:
    | 'MM.DD'
    | 'YY.MM'
    | 'YYYY-MM-DD HH:mm:ss'
    | 'YYYY-MM-DD'
    | 'RAW' = 'MM.DD'
): string => {
  if (!isoLike) return '';

  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return isoLike;

  const kst = utcToZonedTime(d, KOREAN_TIME_ZONE);

  switch (fmt) {
    case 'MM.DD':
      return format(kst, 'MM.dd');
    case 'YY.MM':
      return format(kst, 'yy.MM');
    case 'YYYY-MM-DD':
      return format(kst, 'yyyy-MM-dd');
    case 'YYYY-MM-DD HH:mm:ss':
      return format(kst, 'yyyy-MM-dd HH:mm:ss');
    case 'RAW':
    default:
      return isoLike;
  }
};
